import React from 'react';
import './Button.css';

/**
 * AURORA BUTTON PRIMITIVE
 * 
 * A token-based button component that never uses hardcoded styles
 * All styling comes from tokens.css
 * 
 * Variants: 'primary', 'secondary', 'outline', 'ghost', 'danger'
 * Sizes: 'sm', 'md', 'lg'
 * 
 * @example
 * ```jsx
 * <Button variant="primary" size="md">Click me</Button>
 * <Button variant="outline" size="sm" disabled>Disabled</Button>
 * <Button variant="ghost" loading>Loading...</Button>
 * ```
 */

const Button = React.forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  type = 'button',
  ...props
}, ref) => {
  const classes = [
    'aurora-button',
    `aurora-button--${variant}`,
    `aurora-button--${size}`,
    fullWidth && 'aurora-button--full-width',
    loading && 'aurora-button--loading',
    disabled && 'aurora-button--disabled',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      {...props}
    >
      {loading && (
        <span className="aurora-button__spinner" aria-hidden="true">
          <svg className="aurora-button__spinner-icon" viewBox="0 0 24 24" fill="none">
            <circle
              className="aurora-button__spinner-circle"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
          </svg>
        </span>
      )}
      {!loading && leftIcon && (
        <span className="aurora-button__icon aurora-button__icon--left">
          {leftIcon}
        </span>
      )}
      <span className={loading ? 'aurora-button__text--loading' : ''}>
        {children}
      </span>
      {!loading && rightIcon && (
        <span className="aurora-button__icon aurora-button__icon--right">
          {rightIcon}
        </span>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
